import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { EnergyBackground } from './components/EnergyBackground';
import { LavenderBranches } from './components/LavenderBranches';
import { HeroAmbientEffects } from './components/HeroAmbientEffects';
import { GiftBackground } from './components/GiftBackground';
import { LifetimeAccessBonus } from './components/LifetimeAccessBonus';
import { FloatingEnergySymbols } from './components/FloatingEnergySymbols';
import { PremiumBonusEffect, TitleSparkles } from './components/PremiumBonusEffect';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  image: 'https://cdn.magicpatterns.com/uploads/4E8eYDWnfkpYBG2afdcu5M/454ebf8f-4321-4538-84ea-d616e90230e4.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/wxNtKWsuN2RmMwYU2JgRRc/18f026a7-f17f-4618-ae8e-46ff64ed4b11.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/dmXs9mokmshvmFFWeT7Uwe/23908e6a-c5ad-4032-b121-e9f5bf0b0d50.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/sMerfcGLjX7D5ZfxHHcNXk/8163cbf9-bbd9-4b6f-bf6f-d27cfa99388d.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/6gEEDUuzyuu7QfWTRiGjaw/e57a8eb9-f363-4862-a011-3ba84541bf54.webp'
}];
const heroImages = ['https://cdn.magicpatterns.com/uploads/7hAbtPEv66tS7cRaYNE4r9/me_gusta_mucho_202604131502.png', 'https://cdn.magicpatterns.com/uploads/pYiKeNHymWKoebcvZXR1Ds/Diseno_sin_titulo_(1).png'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '2048912638989861';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FAF7FF] text-violet-950 font-sans selection:bg-violet-200 selection:text-violet-950" data-id="element-1">
      <EnergyBackground data-id="element-2" />
      <LavenderBranches data-id="element-3" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-4" />

      {/* Floating Energy Symbols Overlay — covers first 3 sections */}
      <div className="relative" data-id="element-5">
        <FloatingEnergySymbols data-id="element-6" />

        {/* Hero Section */}
        <header className="relative overflow-hidden bg-[#0f051a]" data-id="element-7">
          <HeroAmbientEffects data-id="element-8" />
          <div className="relative" data-id="element-9">
            <img src="https://cdn.magicpatterns.com/uploads/oJCiF6sQmvdkShYfErn2iQ/48e9fc2d-6e4d-4a98-8851-c5eef791fbd6.webp" alt="Conviértete en Terapeuta de Radiestesia" className="w-full h-auto block" data-id="element-10" />

            {/* Premium CTA Button Overlay */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-full max-w-4xl px-4 text-center" data-id="element-11">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="flex flex-col items-center relative" data-id="element-12">
                {/* Premium Breathing Glow Behind CTA */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] bg-gradient-to-r from-[#D4AF37]/20 via-[#A78BFA]/20 to-[#D4AF37]/20 blur-2xl rounded-full pointer-events-none" animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [0.9, 1.1, 0.9]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-13" />

                <motion.button onClick={scrollToPurchase} animate={{
                y: [0, -8, 0],
                boxShadow: ['0 10px 25px -5px rgba(167, 139, 250, 0.3)', '0 20px 35px -5px rgba(212, 175, 55, 0.4)', '0 10px 25px -5px rgba(167, 139, 250, 0.3)']
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative group px-6 py-2 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F9E29C] to-[#A78BFA] text-violet-950 font-black text-sm md:text-base tracking-wider uppercase border border-white/40 overflow-hidden transition-transform active:scale-95 shadow-2xl mt-[0px] pt-[0px] pb-[0px] mb-[35px]" data-id="element-14">
                  {/* Glossy Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-black/5 pointer-events-none" data-id="element-15" />

                  {/* Shine Effect */}
                  <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatDelay: 2
                }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 pointer-events-none" data-id="element-16" />

                  <span className="relative z-10 flex items-center gap-3 text-[14px]" data-id="element-17">
                    QUIERO APRENDER RADIESTESIA AHORA
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-18" />
                  </span>
                </motion.button>
              </motion.div>
            </div>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/qyTMN5SSNTxsBfRjrSgBip/1add1fe7-44b1-4f2e-ae59-8922e9cbb57e.webp" alt="Metodología de Radiestesia" className="w-full h-auto block" data-id="element-19" />
          <img src="https://cdn.magicpatterns.com/uploads/mG7ZJTvE9uon6cCCKyvXGF/48e59c4f-ede7-42f3-b9a2-0dabbb1b4373.webp" alt="Kit Completo de Radiestesia" className="w-full h-auto block" data-id="element-20" />
        </header>

        <WhatYouReceive data-id="element-21" />

        {/* Intermediate CTA */}
        <section className="py-12 text-center relative z-10 bg-[#FFFBF2] pt-[10px] pb-[10px]" data-id="element-22">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-container px-4" data-id="element-23">
            <motion.button onClick={scrollToPurchase} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="relative group px-10 py-5 rounded-2xl bg-gradient-to-r from-[#D4AF37] via-[#F9E29C] to-[#A78BFA] text-violet-950 font-black text-lg md:text-xl tracking-wider uppercase shadow-xl hover:shadow-violet-200/50 transition-all overflow-hidden border border-white/40" data-id="element-24">
              {/* Shine Effect */}
              <motion.div animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
              repeatDelay: 2
            }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none" data-id="element-25" />
              <span className="relative z-10 flex items-center justify-center gap-3" data-id="element-26">
                QUIERO MI KIT PROFESIONAL AHORA
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" data-id="element-27" />
              </span>
            </motion.button>
            <p className="mt-6 text-violet-500 font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-28">
              <ShieldCheck size={16} data-id="element-29" />
              Acceso inmediato y seguro
            </p>
          </motion.div>
        </section>

        {/* Testimonials */}
        <section className="py-32 relative overflow-hidden bg-gradient-to-b from-[#F5F0FF] to-[#FDF6F8] pt-[5px] pb-[5px]" data-id="element-30">
          {/* Soft blur background elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FDF6F8]/80 via-transparent to-transparent pointer-events-none" data-id="element-31" />
          <div className="absolute -left-40 top-40 w-96 h-96 bg-[#F5E1EA]/40 rounded-full blur-3xl pointer-events-none" data-id="element-32" />
          <div className="absolute -right-40 bottom-20 w-96 h-96 bg-[#FDF6F8] rounded-full blur-3xl shadow-[0_0_100px_rgba(212,175,55,0.08)] pointer-events-none" data-id="element-33" />

          <div className="max-container px-4 relative z-10" data-id="element-34">
            <div className="text-center mb-16 max-w-3xl mx-auto relative" data-id="element-35">
              <div className="absolute -top-8 left-0 md:-left-8 text-[#D4AF37] opacity-70" data-id="element-36">
                <Sparkles size={32} fill="currentColor" data-id="element-37" />
              </div>
              <span className="text-violet-600 font-black uppercase tracking-[0.3em] mb-6 block text-xs md:text-sm" data-id="element-38">
                Testimonios Reales
              </span>
              <h3 className="text-4xl md:text-7xl text-violet-950 tracking-tight font-medium font-['Playfair_Display'] mb-8 leading-[1.1]" data-id="element-39">
                <span className="text-violet-600 italic" data-id="element-40">Transforma</span> tus
                <br data-id="element-41" />
                sesiones hoy
              </h3>
              <p className="text-violet-500 font-medium text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-42">
                Mira cómo terapeutas como tú ya están logrando resultados
                rápidos y profundos con nuestras herramientas.
              </p>
            </div>

            <div className="max-w-4xl mx-auto relative" data-id="element-43">
              <div className="overflow-hidden" data-id="element-44">
                <motion.div key={currentTestimonial} drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} onDragEnd={(_, info) => {
                if (info.offset.x > 100) {
                  prevTestimonial();
                } else if (info.offset.x < -100) {
                  nextTestimonial();
                }
              }} initial={{
                opacity: 0,
                x: 100
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="bg-[#FFFBF2] p-4 md:p-8 rounded-[2rem] shadow-[0_30px_60px_-12px_rgba(167,139,250,0.18)] border border-[#EDE4FF] relative backdrop-blur-sm cursor-grab active:cursor-grabbing" data-id="element-45">
                  <div className="flex flex-col items-center gap-6" data-id="element-46">
                    <div className="flex justify-center text-[#D4AF37] gap-1" data-id="element-47">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={24} fill="currentColor" strokeWidth={1} data-id="element-48" />)}
                    </div>

                    <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-[#EDE4FF] shadow-inner" data-id="element-49">
                      <img src={testimonials[currentTestimonial].image} alt="Testimonio" className="w-full h-auto object-contain" data-id="element-50" />
                    </div>

                    <div className="text-center" data-id="element-51">
                      <span className="inline-block bg-[#F5E1EA] text-[#9D5A75] text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full" data-id="element-52">
                        Experiencia Real Verificada
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Navigation Arrows */}
              <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-[#FFFBF2] p-4 rounded-full shadow-xl text-violet-600 hover:bg-[#F5F0FF] transition-colors z-20 hidden md:block" data-id="element-53">
                <ArrowRight className="rotate-180" size={24} data-id="element-54" />
              </button>
              <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-[#FFFBF2] p-4 rounded-full shadow-xl text-violet-600 hover:bg-[#F5F0FF] transition-colors z-20 hidden md:block" data-id="element-55">
                <ArrowRight size={24} data-id="element-56" />
              </button>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-3 mt-12" data-id="element-57">
                {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-[#A78BFA]' : 'w-3 bg-[#EDE4FF] hover:bg-[#D6C6F5]'}`} data-id="element-58" />)}
              </div>

              {/* Sliding Instruction */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} className="flex flex-col items-center gap-2 mt-8 md:hidden" data-id="element-59">
                <div className="flex items-center gap-4 text-violet-400" data-id="element-60">
                  <motion.div animate={{
                  x: [-5, 5, -5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-61">
                    <ArrowRight className="rotate-180" size={16} data-id="element-62" />
                  </motion.div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]" data-id="element-63">
                    Desliza para ver más
                  </span>
                  <motion.div animate={{
                  x: [5, -5, 5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-64">
                    <ArrowRight size={16} data-id="element-65" />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section className="py-24 bg-[#F5F0FF] pt-[40px] pb-[40px] relative overflow-hidden group/bonuses" data-id="element-66">
          <PremiumBonusEffect data-id="element-67" />
          <GiftBackground data-id="element-68" />

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-yellow-200/10 via-transparent to-transparent opacity-0 group-hover/bonuses:opacity-100 transition-opacity duration-1000 pointer-events-none" data-id="element-69" />

          <div className="max-container px-4 relative z-10" data-id="element-70">
            <div className="text-center mb-16 relative" data-id="element-71">
              <TitleSparkles data-id="element-72" />
              <motion.div animate={{
              rotate: [-8, 8, -8],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-block mb-6 text-violet-600" data-id="element-73">
                <Gift size={80} strokeWidth={1.5} data-id="element-74" />
              </motion.div>

              <h3 className="md:text-7xl text-violet-950 tracking-tight text-[42px] font-['Playfair_Display'] mb-6 font-[500] leading-tight" data-id="element-75">
                ¡Regalos{' '}
                <span className="text-violet-600 italic" data-id="element-76">Exclusivos!</span>
              </h3>

              <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-flex items-center gap-3 px-8 py-3 rounded-full bg-violet-600 text-white text-sm md:text-lg font-black uppercase tracking-[0.25em] shadow-2xl shadow-violet-300/50" data-id="element-77">
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-78" />
                <span data-id="element-79">100% GRATIS SOLO POR HOY</span>
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-80" />
              </motion.div>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-81">
              <BonusCard number="01" title="Gráficos Profesionales de Radiestesia" image="https://cdn.magicpatterns.com/uploads/fWH95PnHKNkWMsTd7g9mXi/b496c878-fd89-40a9-b8f2-fd99fe7aa423.webp" description="Utilizá gráficos radiestésicos listos para sesiones, medición energética y armonización." value="$47 USD" data-id="element-82" />
              <BonusCard number="02" title="Planilla Terapéutica de Seguimiento" image="https://cdn.magicpatterns.com/uploads/bxUEDFrzJ5EkaNRh9BsxcQ/5a6606fc-c970-40fd-b474-499753f7df0f.webp" description="Organizá consultas, evolución energética y observaciones de cada cliente." value="$37 USD" data-id="element-83" />
              <BonusCard number="03" title="Cuaderno de Prácticas Radiestésicas" image="https://cdn.magicpatterns.com/uploads/q7ufTu9t8SmcRX8BNXgKhj/3a00b4de-e208-4b45-8d3a-9d9315265ed6.webp" description="Ejercicios guiados para desarrollar precisión, intuición y confianza con el péndulo." value="$27 USD" data-id="element-84" />
              <BonusCard number="04" title="+100 Preguntas Terapéuticas" image="https://cdn.magicpatterns.com/uploads/xiGaUkJeBd81m7UWremYi7/52b42563-aec8-4d14-9170-a3f9724517d5.webp" description="Preguntas diseñadas para profundizar sesiones y detectar bloqueos energéticos." value="$37 USD" data-id="element-85" />
              <BonusCard number="05" title="Guía Profesional del Uso de Péndulo" image="https://cdn.magicpatterns.com/uploads/j2ZW3GthfasKqWVYgd1ECf/0aac60a9-181b-4eff-aa68-cf837eafcf2d.webp" description="Aprendé técnicas, movimientos, limpieza y uso correcto del péndulo paso a paso." value="$27 USD" data-id="element-86" />
            </div>
          </div>
        </section>
      </div>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-[#FAF4FF] relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-87">
        <div className="max-w-4xl mx-auto px-4 relative z-10 space-y-[15px]" data-id="element-88">
          <div className="bg-gradient-to-r from-[#F9E29C] via-[#FCEFC7] to-[#F9E29C] text-violet-950 p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse rounded-xl border border-[#E5C76B]/40 shadow-sm" data-id="element-89">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-90">
            {['Descargue todos los archivos en formato PDF, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-[#EDE4FF] shadow-sm" data-id="element-91">
                <CheckCircle2 className="text-violet-600 shrink-0" size={20} data-id="element-92" />
                <p className="text-violet-900 text-sm font-bold leading-tight" data-id="element-93">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-94">
            <div className="bg-[#FFFBF2] border-[6px] border-[#D4AF37]/50 rounded-[3rem] shadow-2xl overflow-hidden" data-id="element-95">
              <div className="p-0" data-id="element-96">
                <div className="relative" data-id="element-97">
                  <img src="https://cdn.magicpatterns.com/uploads/aSTNCx5fwV4qfBoHpoAzzg/1b00af68-f628-437e-909d-dc301a18f692.webp" alt="Paquete Completo Radiestesia Terapéutica" className="w-full h-auto" data-id="element-98" />
                </div>

                <div className="p-8 md:p-12 text-center pt-[0px] pb-[0px] pl-[5px] pr-[5px] space-y-[15px]" data-id="element-99">
                  <div className="space-y-4" data-id="element-100">
                    <p className="text-violet-400 text-xs font-black uppercase tracking-[0.2em]" data-id="element-101">
                      + {joinedCount} ALUMNOS
                    </p>
                    <div className="flex flex-col items-center gap-2" data-id="element-102">
                      <div className="flex text-yellow-400 gap-1" data-id="element-103">
                        {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="currentColor" data-id="element-104" />)}
                      </div>
                      <span className="text-violet-300 text-sm font-bold" data-id="element-105">
                        (1.728 reseñas)
                      </span>
                    </div>
                  </div>

                  <h3 className="md:text-5xl font-black text-violet-950 tracking-tight text-[20px]" data-id="element-106">
                    Kit Radiestesia Terapéutica PDF + 5 Bonos de regalo Hoy ✨
                  </h3>

                  <div className="space-y-2" data-id="element-107">
                    <p className="line-through font-bold text-[20px] text-[#FF7762]" data-id="element-108">
                      $59.900 ARS
                    </p>
                    <p className="md:text-7xl font-black text-[#319E0D] tracking-tighter text-[48px]" data-id="element-109">
                      $19.990 ARS
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-110">
                    <div className="relative overflow-hidden bg-violet-50 border border-violet-100 rounded-2xl p-4 shadow-sm max-w-sm mx-auto group" data-id="element-111">
                      {/* Shimmer Effect */}
                      <motion.div animate={{
                      x: ['-100%', '200%']
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 1.5,
                      ease: 'easeInOut'
                    }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 pointer-events-none z-10" data-id="element-112" />

                      <div className="relative z-20 flex flex-col items-center gap-2" data-id="element-113">
                        <div className="inline-flex items-center gap-2 px-4 py-1 bg-violet-600 text-white rounded-full font-black uppercase tracking-widest shadow-md text-[10px]" data-id="element-114">
                          <Sparkles size={14} className="text-yellow-300" data-id="element-115" />
                          66% OFF HOY
                        </div>
                        <p className="text-violet-950 font-bold text-lg" data-id="element-116">
                          3 cuotas sin interés de{' '}
                          <span className="text-[#319E0D]" data-id="element-117">$6.663</span>
                        </p>
                      </div>
                    </div>

                    <div className="relative group" data-id="element-118">
                      {/* Magical Glow Background */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-green-400 to-yellow-400 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200 animate-pulse" data-id="element-119"></div>

                      <a href="https://codex-labs.shop/cart/43541449310267:1" target="_blank" rel="noopener noreferrer" className="relative w-full bg-[#82C91E] text-white py-6 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-[#74b816] transition-all shadow-[0_10px_20px_rgba(130,201,30,0.3)] hover:scale-[1.02] active:scale-95 uppercase tracking-tight overflow-hidden text-[28px]" data-id="element-120">
                        {/* Shimmer Effect */}
                        <motion.div animate={{
                        x: ['-100%', '200%']
                      }} transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'linear',
                        repeatDelay: 1
                      }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12" data-id="element-121" />
                        ACCEDER AHORA
                      </a>
                    </div>

                    <motion.div animate={{
                    scale: [1, 1.05, 1],
                    opacity: [1, 0.8, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} className="flex items-center justify-center gap-2 text-violet-400 font-bold" data-id="element-122">
                      <span className="text-xl" data-id="element-123">🔥</span>
                      <span data-id="element-124">
                        ¡Últimos{' '}
                        <motion.span key={spots} initial={{
                        scale: 1.5,
                        color: '#ef4444'
                      }} animate={{
                        scale: 1,
                        color: 'inherit'
                      }} transition={{
                        duration: 0.5,
                        type: 'spring'
                      }} className="inline-block" data-id="element-125">
                          {spots}
                        </motion.span>{' '}
                        cupos en oferta!
                      </span>
                    </motion.div>

                    <div className="mt-4 -mx-8 md:-mx-12" data-id="element-126"></div>
                  </div>
                </div>

                {/* Trust Section */}
                <div className="p-8 md:p-12 bg-violet-50/50 border-t-2 border-dashed border-violet-100 space-y-[5px]" data-id="element-127">
                  {/* Primary Trust Signals - Large and Stacked */}
                  <div className="text-center space-y-[0px]" data-id="element-128">
                    <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="h-40 md:h-56 mx-auto object-contain shadow-md rounded-2xl" data-id="element-129" />
                    <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[450px] mx-auto object-contain" data-id="element-130" />
                  </div>

                  {/* Secondary Trust Signals - Row */}
                  <div className="border-t border-violet-50 pt-[0px] mt-[0px]" data-id="element-131">
                    <div className="flex md:gap-20 gap-[0px] flex-row items-center justify-center" data-id="element-132">
                      <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 object-contain" data-id="element-133" />
                      <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-24 md:h-32 object-contain" data-id="element-134" />
                    </div>
                  </div>
                </div>
                <div className="-mx-[6px] -mb-[6px]" data-id="element-135">
                  <img src="https://cdn.magicpatterns.com/uploads/v8j4Lt9vmm43yrw1WX4iKu/c0009f87-e89c-4e3d-8468-fdb8d358afac.png" alt="Regalos Exclusivos Solo Por Hoy" className="w-full h-auto" data-id="element-136" />
                </div>
              </div>
            </div>
          </div>
          <LifetimeAccessBonus data-id="element-137" />
        </div>
      </section>

      {/* Creator Section */}
      <section className="bg-[#FFFBF2]" data-id="element-138">
        <img src="https://cdn.magicpatterns.com/uploads/h7PovxhuGKKpp4tgHZ9DVY/hf_20260522_220052_2bf523b5-44a1-4bec-bb01-c674a879982c.webp" alt="Sofía Alondra - Creadora del Kit Radiestesia Terapéutica" className="w-full h-auto block" data-id="element-139" />
      </section>

      <Reviews data-id="element-140" />

      <FAQ data-id="element-141" />

      <footer className="py-12 border-t border-[#EDE4FF] text-center text-violet-600 text-sm" data-id="element-142">
        <p data-id="element-143">© 2025 Portal Terapéutico. Todos los derechos reservados.</p>
      </footer>
    </div>;
}